import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Paper, TextField } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Autocomplete, {
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState,
} from '@material-ui/lab/Autocomplete'
import { editContainerFontColor } from 'bpm/shared/bpmColors'
import React, {
    ChangeEvent,
    ReactNode,
    forwardRef,
    useMemo,
    useRef,
} from 'react'

export interface AutoCompleteInputProps {
    name: string
    items: any[]
    getOptionLabel?: (option: any) => string
    groupBy?: (option: any) => string
    label?: string
    loading?: boolean
    selectedValue?: any
    disabled?: boolean
    valid?: boolean
    helperText?: string
    clearOnBlur?: boolean
    disableClearable?: boolean
    open?: boolean
    onClose?: () => void
    renderFooter?: () => ReactNode
    customOptions?:
        | ((option: string, state: AutocompleteRenderOptionState) => ReactNode)
        | undefined
    handleChange: (event: ChangeEvent<{}>, value: string | null) => void
    handleInputChange: (
        event: ChangeEvent<{}>,
        value: string,
        reason?: string
    ) => void
    handleOpen?: (event: ChangeEvent<{}>) => void
    getOptionSelected?: (option: any, value: any) => boolean
}
interface AutoCompleteInputMultiSelectProps
    extends Omit<AutoCompleteInputProps, 'handleChange'> {
    handleChange: (event: ChangeEvent<{}>, value: string[]) => void
    selectedValues?: any[]
    placeholder?: string
    noLabel?: boolean
    variant?: 'outlined' | 'standard' | 'filled'
    renderInput?: (params: AutocompleteRenderInputParams) => ReactNode
    [key: string]: any
}
const useStyles = makeStyles((theme: Theme) => ({
    autoCompleteInput: {
        backgroundColor: theme.palette.common.white,
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            padding: `${theme.spacing(0.53)}px ${theme.spacing(
                8.1
            )}px ${theme.spacing(0.6)}px ${theme.spacing(1)}px`,
        },
        '& .MuiAutocomplete-clearIndicator': {
            marginTop: -theme.spacing(0.5),
        },
        '& label': {
            transform: `translate(${theme.spacing(1.75)}px, ${theme.spacing(
                2
            )}px) scale(1)`,
        },
        '& .MuiAutocomplete-groupLabel': {
            color: editContainerFontColor,
            fontSize: pxToRem(12),
        },
    },
    autocompleteAdornment: {
        maxHeight: pxToRem(50),
        overflowY: 'auto', // show vertical scrollbar when many items selected
    },
    autocompleteMultiSelectAdornment: {
        maxHeight: pxToRem(75),
        overflowY: 'auto',
    },
}))

export const AutoCompleteInput: React.FC<AutoCompleteInputProps> = ({
    name,
    items,
    label,
    selectedValue,
    loading,
    disabled = false,
    valid,
    helperText,
    customOptions,
    handleOpen,
    handleInputChange,
    handleChange,
    getOptionSelected,
    clearOnBlur,
    disableClearable = false,
    open,
    onClose,
    renderFooter,
    getOptionLabel,
}) => {
    const classes = useStyles()

    const renderFooterRef = useRef(renderFooter)
    renderFooterRef.current = renderFooter

    const PaperWithFooter = useMemo(
        () =>
            forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
                (props, ref) => (
                    <Paper ref={ref} {...props}>
                        {props.children}
                        {renderFooterRef.current?.()}
                    </Paper>
                )
            ),
        []
    )

    return (
        <Autocomplete
            data-testid="autoComplete"
            options={items}
            onInputChange={handleInputChange}
            getOptionLabel={getOptionLabel}
            clearOnBlur={clearOnBlur}
            value={selectedValue}
            onChange={handleChange}
            onOpen={handleOpen}
            onClose={onClose}
            open={open}
            disabled={disabled}
            disableClearable={disableClearable}
            freeSolo={false}
            loading={loading}
            PaperComponent={PaperWithFooter}
            renderInput={(params) => {
                const { InputProps, ...restParams } = params
                const { startAdornment, ...restInputProps } = InputProps
                return (
                    <>
                        {/* for test cases */}
                        <input
                            style={{ display: 'none' }}
                            type="text"
                            name={`fake-${name}`}
                        />
                        <TextField
                            {...restParams}
                            className={classes.autoCompleteInput}
                            error={!valid}
                            helperText={helperText}
                            inputProps={{
                                ...params.inputProps,
                                'aria-autocomplete': 'none',
                                autoComplete: 'off',
                            }}
                            InputProps={{
                                ...restInputProps,
                            }}
                            id={`${name}-input`}
                            data-testid={`${name}-input-test`}
                            name={name}
                            label={label ?? name}
                            variant="outlined"
                        />
                    </>
                )
            }}
            renderOption={customOptions}
            getOptionSelected={getOptionSelected}
        />
    )
}

export const AutoCompleteInputMultiSelect: React.FC<AutoCompleteInputMultiSelectProps> = ({
    name,
    items,
    label,
    noLabel,
    placeholder,
    handleChange,
    loading,
    selectedValues,
    disabled = false,
    groupBy,
    getOptionLabel,
    handleInputChange,
    renderInput,
    valid,
    variant = 'outlined',
    collateValues = false,
    getOptionSelected,
    ...props
}) => {
    const classes = useStyles()
    const inputRef = useRef<HTMLElement>()

    const defaultRenderInput = (params: AutocompleteRenderInputParams) => {
        const { InputProps, InputLabelProps, ...restParams } = params
        const { startAdornment, ...restInputProps } = InputProps
        return (
            <>
                {/* for test cases */}
                <input
                    style={{ display: 'none' }}
                    type="text"
                    name={`fake${name}`}
                />
                <TextField
                    {...restParams}
                    inputProps={{
                        ...params.inputProps,
                        'aria-autocomplete': 'none',
                        autoComplete: 'off',
                    }}
                    inputRef={inputRef}
                    InputLabelProps={{
                        ...InputLabelProps,
                        shrink:
                            inputRef.current === document.activeElement ||
                            !!selectedValues?.length,
                    }}
                    placeholder={!selectedValues?.length ? placeholder : ''}
                    error={!valid}
                    InputProps={{
                        ...restInputProps,
                        startAdornment: (
                            <div
                                className={
                                    classes.autocompleteMultiSelectAdornment
                                }
                            >
                                {startAdornment}
                            </div>
                        ),
                    }}
                    id={`${name}-input`}
                    data-testid={`${name}-input-test`}
                    name={`input${name}`}
                    label={noLabel ? '' : label ?? name}
                    variant={variant}
                    style={{ zIndex: 1, display: 'block' }}
                />
            </>
        )
    }

    const renderInputFn = useMemo(
        () => (!!renderInput ? renderInput : defaultRenderInput),
        [renderInput, defaultRenderInput]
    )

    return (
        <Autocomplete
            {...props}
            data-testid="autoCompleteMultiSelect"
            options={items}
            onInputChange={handleInputChange}
            onChange={handleChange}
            multiple
            freeSolo={false}
            groupBy={groupBy}
            getOptionLabel={getOptionLabel}
            className={classes.autoCompleteInput}
            disabled={disabled}
            value={selectedValues}
            loading={loading}
            renderInput={renderInputFn}
            getOptionSelected={getOptionSelected}
        />
    )
}
